import type { TaskCollectionChangedNotification } from 'types/pushNotifications';

type TaskCollectionRefreshListener = (notification: TaskCollectionChangedNotification) => void;

const listeners = new Set<TaskCollectionRefreshListener>();
const reconnectListeners = new Set<() => void>();

export const TASK_COLLECTION_REFRESH_TIMEOUT_MS = 30_000;

export type TaskCollectionRefreshResult = 'refreshed' | 'timed-out';

export const emitTaskCollectionRefresh = (notification: TaskCollectionChangedNotification) => {
  listeners.forEach((listener) => listener(notification));
};

export const subscribeToTaskCollectionRefresh = (listener: TaskCollectionRefreshListener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const emitTaskCollectionReconnect = () => {
  reconnectListeners.forEach((listener) => listener());
};

export const subscribeToTaskCollectionReconnect = (listener: () => void) => {
  reconnectListeners.add(listener);
  return () => {
    reconnectListeners.delete(listener);
  };
};

export const waitForTaskCollectionRefresh = (
  campaignId: string,
  contractId?: string,
  timeoutMs = TASK_COLLECTION_REFRESH_TIMEOUT_MS
) => {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let resolved = false;
  let resolveRefresh!: (result: TaskCollectionRefreshResult) => void;
  const promise = new Promise<TaskCollectionRefreshResult>((resolve) => {
    resolveRefresh = resolve;
  });
  const cancel = () => {
    if (timeout !== undefined) clearTimeout(timeout);
    listeners.delete(listener);
  };
  const resolve = (result: TaskCollectionRefreshResult) => {
    if (resolved) return;

    resolved = true;
    cancel();
    resolveRefresh(result);
  };
  const listener: TaskCollectionRefreshListener = (notification) => {
    if (notification.campaignId !== campaignId || notification.contractId !== contractId) return;

    resolve('refreshed');
  };
  const wait = () => {
    if (!resolved && timeout === undefined) {
      timeout = setTimeout(() => resolve('timed-out'), timeoutMs);
    }

    return promise;
  };

  listeners.add(listener);
  return {
    wait,
    cancel,
  };
};
